import { Dispatch, SetStateAction, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import {
  Confirmation,
  VerticalTabs,
  Visible,
  withFilters,
  withSelectionPanel,
} from 'coreOld/components';
import { ButtonType, FormActions, Size } from 'coreOld/enums';
import { IFiltersProps, ISelectionPanelProps, ITab } from 'coreOld/types';
import { UsersManager, UsersRanks } from 'system/components';
import { UsersOptions } from 'system/constants';
import { IRank, ISystemUser } from 'system/types';

import { UsersTabs } from './constants';
import UsersForm from './UsersForm';
import {
  getConfirmationButtonText,
  getConfirmationMessage,
  getTitleForConfirmation,
} from './utils';

interface IProps extends IFiltersProps, ISelectionPanelProps<ISystemUser> {
  activeTab?: ITab;
  tabItems?: ITab[];
  setActiveTab?: Dispatch<SetStateAction<ITab>>;
  selectedItem?: FormActions;
  setSelectedItem?: Dispatch<SetStateAction<IRank | string | null>>;
  selectOptionsConfig;
  selectedOption?: UsersOptions;
  setSelectedOption?: Dispatch<SetStateAction<UsersOptions>>;
  confirmationOption?: UsersOptions;
  setConfirmationOption?: Dispatch<SetStateAction<UsersOptions>>;
  onConfirmBulkAction?: (users: ISystemUser[], option: UsersOptions) => void;
  closeSelection: boolean;
  setCloseSelection: Dispatch<SetStateAction<boolean>>;
}

const UsersView = ({
  activeTab,
  setActiveTab,
  tabItems,
  filters,
  setFilters,
  selectedItem,
  setSelectedItem,
  setSelectedRows,
  selectedRows,
  isModalOpen,
  selectedOption,
  setSelectedOption,
  confirmationOption,
  setConfirmationOption,
  onCheckboxClick,
  onConfirmBulkAction,
  setCloseSelection,
}: IProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    setFilters({});
  }, [activeTab]);

  return (
    <>
      <Helmet>
        <title>{t('titleSystemSettingsUsersPage')}</title>
      </Helmet>

      <UsersForm
        selectedRows={selectedRows}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        setCloseSelection={setCloseSelection}
      />

      <Visible when={Boolean(confirmationOption)}>
        <Confirmation
          isOpen={Boolean(confirmationOption)}
          onConfirm={() => onConfirmBulkAction(selectedRows, confirmationOption)}
          onCancel={() => setConfirmationOption(null)}
          confirmBtnLabel={getConfirmationButtonText(confirmationOption)}
          confirmBtnType={ButtonType.DANGER}
          size={Size.MD}
          title={getTitleForConfirmation(confirmationOption)}
          message={getConfirmationMessage(confirmationOption, selectedRows)}
        />
      </Visible>

      <VerticalTabs tabs={tabItems} activeTab={activeTab} setActiveTab={setActiveTab}>
        <Visible when={activeTab?.title === UsersTabs.MANAGE_USERS}>
          <UsersManager
            filters={filters}
            setSelectedRows={setSelectedRows as unknown as Dispatch<SetStateAction<ISystemUser[]>>}
            isModalOpen={isModalOpen}
            onCheckboxClick={onCheckboxClick}
          />
        </Visible>

        <Visible when={activeTab?.title === UsersTabs.RANKS}>
          <UsersRanks
            filters={filters}
            selectedItem={selectedItem}
            setSelectedItem={
              setSelectedItem as Dispatch<SetStateAction<FormActions | IRank | null>>
            }
          />
        </Visible>
      </VerticalTabs>
    </>
  );
};

export default withSelectionPanel(withFilters(UsersView));
